import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';

export interface SavedPaymentMethod {
  stripe_payment_method_id: string;
  card_brand: string | null;
  card_last4: string | null;
  card_exp_month: number | null;
  card_exp_year: number | null;
  is_default: boolean | null;
  status: string | null;
  type: string;
}

export function usePaymentMethods() {
  const queryClient = useQueryClient();
  const [userId, setUserId] = useState<string | null>(null);

  // Track previous user ID to detect account switches
  const previousUserIdRef = useRef<string | null>(null);

  // Listen for auth changes so the query auto-refreshes on login/logout
  useEffect(() => {
    const fetchUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const currentUserId = session?.user?.id ?? null;
      previousUserIdRef.current = currentUserId;
      setUserId(currentUserId);
    };
    fetchUser();

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      const newUserId = session?.user?.id ?? null;
      const previousUserId = previousUserIdRef.current;
      
      // If user changed (switched accounts), clear all payment method caches
      if (previousUserId !== null && previousUserId !== newUserId) {
        console.log('[usePaymentMethods] User switched, clearing payment method cache');
        queryClient.removeQueries({ queryKey: ['payment-methods'] });
      }
      
      previousUserIdRef.current = newUserId;
      setUserId(newUserId);
    });

    return () => {
      listener?.subscription.unsubscribe();
    };
  }, [queryClient]);

  const query = useQuery({
    queryKey: ['payment-methods', userId], // Include user ID in cache key for security
    queryFn: async () => {
      if (!userId) return [];

      const { data, error } = await supabase
        .from('payment_methods')
        .select('stripe_payment_method_id, card_brand, card_last4, card_exp_month, card_exp_year, is_default, status, type')
        .eq('user_id', userId)
        .eq('type', 'card')
        .neq('status', 'detached')
        .order('is_default', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching payment methods:', error);
        throw error;
      }

      return (data as SavedPaymentMethod[]) || [];
    },
    enabled: !!userId, // Only enable when user is logged in
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: true, // Always refetch when component mounts to get latest payment methods
  });

  const setDefaultMutation = useMutation({
    mutationFn: async ({ paymentMethodId, customerId }: { paymentMethodId: string, customerId: string }) => {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(
        'https://wgghxlrehnnejmvzzsmx.functions.supabase.co/set-default-payment-method',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ customerId, paymentMethodId }),
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to set default payment method');
      }

      return response.json();
    },
    onSuccess: async () => {
      // Invalidate for current user only
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (userId) {
        queryClient.invalidateQueries({ queryKey: ['payment-methods', userId] });
      }
    },
  });

  return {
    ...query,
    paymentMethods: query.data || [],
    defaultPaymentMethod: query.data?.find(pm => pm.is_default) || null,
    setDefaultPaymentMethod: setDefaultMutation.mutate,
    isSettingDefault: setDefaultMutation.isPending,
  };
}

export function useCustomer() {
  return useQuery({
    queryKey: ['stripe-customer'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(
        'https://wgghxlrehnnejmvzzsmx.functions.supabase.co/get-or-create-customer',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to get customer');
      }

      const result = await response.json();
      return result.customerId;
    },
    enabled: true,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}